import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdAdd, MdDelete, MdArrowBack } from 'react-icons/md';

const FIELD_TYPES = ['string', 'select', 'multi-select', 'number'];

export default function FormFieldForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        type: 'string',
        question: '',
        options: [] as string[],
        blockedAnswers: [] as string[],
        required: false,
    });

    const [newOption, setNewOption] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchFormField = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/form-field/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData({
                id: response.data.id,
                name: response.data.name,
                type: response.data.type,
                question: response.data.question || '',
                options: response.data.options || [],
                blockedAnswers: response.data.blockedAnswers || [],
                required: response.data.required || false,
            });
        } catch (error) {
            console.error('Error fetching form field:', error);
            alert('Failed to load form field');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isEdit) {
            fetchFormField();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.id.trim()) {
            alert('Field ID is required');
            return;
        }

        if (!formData.name.trim()) {
            alert('Field name is required');
            return;
        }

        if ((formData.type === 'select' || formData.type === 'multi-select') && formData.options.length === 0) {
            alert('At least one option is required for select/multi-select fields');
            return;
        }

        try {
            setSaving(true);
            const token = localStorage.getItem('token');
            
            console.log('Saving form field with data:', formData);
            
            if (isEdit) {
                await axios.put(`${CONFIG.apiUrl}/form-field/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${CONFIG.apiUrl}/form-field`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            
            navigate('/back-office/form-fields');
        } catch (error: any) {
            console.error('Error saving form field:', error);
            alert(error.response?.data?.message || 'Failed to save form field');
        } finally {
            setSaving(false);
        }
    };

    const addOption = () => {
        if (newOption.trim() && !formData.options.includes(newOption.trim())) {
            setFormData({ ...formData, options: [...formData.options, newOption.trim()] });
            setNewOption('');
        }
    };

    const removeOption = (index: number) => {
        const removedOption = formData.options[index];
        const newBlockedAnswers = formData.blockedAnswers.filter(answer => answer !== removedOption);
        setFormData({ 
            ...formData, 
            options: formData.options.filter((_, i) => i !== index),
            blockedAnswers: newBlockedAnswers
        });
    };

    const moveOption = (index: number, direction: 'up' | 'down') => {
        const newOptions = [...formData.options];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        
        if (targetIndex >= 0 && targetIndex < newOptions.length) {
            [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
            setFormData({ ...formData, options: newOptions });
        }
    };

    const toggleBlockedAnswer = (option: string) => {
        const isBlocked = formData.blockedAnswers.includes(option);
        if (isBlocked) {
            setFormData({ 
                ...formData, 
                blockedAnswers: formData.blockedAnswers.filter(answer => answer !== option) 
            });
        } else {
            setFormData({ 
                ...formData, 
                blockedAnswers: [...formData.blockedAnswers, option] 
            });
        }
    };

    const needsOptions = formData.type === 'select' || formData.type === 'multi-select';

    if (loading) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px]">
            <Card extra="p-5">
                <div className="mb-5 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/form-fields')}
                        className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        {isEdit ? 'Edit Form Field' : 'Create Form Field'}
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-6">
                        <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                            Basic Information
                        </h3>
                        
                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Field ID *
                                <span className="ml-2 text-xs text-gray-500">(Used in forms and APIs)</span>
                            </label>
                            <input
                                type="text"
                                value={formData.id}
                                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                placeholder="e.g., Prénom, Email, Téléphone"
                                required
                                disabled={isEdit}
                            />
                            {isEdit && (
                                <p className="mt-1 text-xs text-gray-500">Field ID cannot be changed</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Display Name *
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                placeholder="Display name for the field"
                                required
                            />
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Field Type *
                            </label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ 
                                    ...formData, 
                                    type: e.target.value,
                                    options: (e.target.value === 'string' || e.target.value === 'number') ? [] : formData.options,
                                    blockedAnswers: (e.target.value === 'string' || e.target.value === 'number') ? [] : formData.blockedAnswers
                                })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                required
                            >
                                {FIELD_TYPES.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Question / Label
                                <span className="ml-2 text-xs text-gray-500">(optional)</span>
                            </label>
                            <textarea
                                value={formData.question}
                                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                rows={2}
                                placeholder="Question to ask the user (e.g., 'Quelle est votre situation familiale ?')"
                            />
                        </div>
                    </div>

                    {/* Options (for select and multi-select) */}
                    {needsOptions && (
                        <div className="mb-6">
                            <h3 className="mb-3 text-lg font-semibold text-navy-700 dark:text-white">
                                Options *
                            </h3>
                            <div className="mb-3 flex gap-2">
                                <input
                                    type="text"
                                    value={newOption}
                                    onChange={(e) => setNewOption(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                                    placeholder="Add an option..."
                                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                                <button
                                    type="button"
                                    onClick={addOption}
                                    className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm text-white hover:bg-brand-600"
                                >
                                    <MdAdd />
                                    Add
                                </button>
                            </div>

                            {formData.options.length === 0 ? (
                                <div className="rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200">
                                    At least one option is required for {formData.type} fields
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {formData.options.map((option, index) => (
                                        <div key={index} className="flex.value items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-700">
                                            <span className="flex-1 text-sm text-gray-800 dark:text-white">{option}</span>
                                            <div className="flex gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => moveOption(index, 'up')}
                                                    disabled={index === 0}
                                                    className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 disabled:opacity-30 dark:text-gray-300 dark:hover:bg-navy-600"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => moveOption(index, 'down')}
                                                    disabled={index === formData.options.length - 1}
                                                    className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 disabled:opacity-30 dark:text-gray-300 dark:hover:bg-navy-600"
                                                >
                                                    ↓
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => removeOption(index)}
                                                    className="rounded px-2 py-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Blocked Answers (for select and multi-select) */}
                    {needsOptions && (
                        <div className="mb-6">
                            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                                            🚫 Gestion des réponses bloquées
                                        </h3>
                                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                            Configurez les réponses qui feront rejeter automatiquement les leads
                                        </p>
                                    </div>
                                    {formData.blockedAnswers.length > 0 && (
                                        <div className="text-right">
                                            <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                                {formData.blockedAnswers.length} / {formData.options.length} bloquées
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-lg border border-gray-200 p-4 dark:border-navy-600">
                                {/* Bulk Actions */}
                                <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600">
                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                        Sélectionnez les réponses à bloquer
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedAnswers: [...formData.options] })}
                                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                                        >
                                            Tout bloquer
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({ ...formData, blockedAnswers: [] })}
                                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                                        >
                                            Tout débloquer
                                        </button>
                                    </div>
                                </div>

                                {/* Options List */}
                                <div className="space-y-2">
                                    {formData.options.map((option, index) => {
                                        const isBlocked = formData.blockedAnswers.includes(option);
                                        return (
                                            <div 
                                                key={index} 
                                                className={`flex items-center gap-3 rounded-lg border p-3 transition-all ${
                                                    isBlocked 
                                                        ? 'border-red-300 bg-red-50 shadow-sm dark:border-red-900/50 dark:bg-red-900/20' 
                                                        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-navy-600 dark:bg-navy-800 dark:hover:border-navy-500'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`blocked-${index}`}
                                                    checked={isBlocked}
                                                    onChange={() => toggleBlockedAnswer(option)}
                                                    className="h-4 w-4 rounded border-gray-300 text-red-500 focus:ring-red-500 dark:border-navy-600 dark:bg-navy-700"
                                                />
                                                <label 
                                                    htmlFor={`blocked-${index}`}
                                                    className={`flex-1 cursor-pointer text-sm ${
                                                        isBlocked 
                                                            ? 'font-semibold text-red-700 dark:text-red-300' 
                                                            : 'text-gray-700 dark:text-gray-300'
                                                    }`}
                                                >
                                                    {option}
                                                </label>
                                                {isBlocked && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-800 dark:bg-red-900/40 dark:text-red-200">
                                                            BLOQUÉE
                                                        </span>
                                                        <span className="text-red-500">✕</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Info Box */}
                                <div className="mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                                    <div className="flex gap-2">
                                        <span className="text-blue-500">ℹ️</span>
                                        <div className="flex-1 text-xs text-blue-700 dark:text-blue-300">
                                            <strong>Note:</strong> Les leads contenant une réponse bloquée seront automatiquement rejetés et renvoyés au vendeur avec le statut "BLOCKED".
                                            {formData.blockedAnswers.length === 0 ? (
                                                <span className="ml-1">Aucune réponse n'est actuellement bloquée.</span>
                                            ) : (
                                                <span className="ml-1">
                                                    {formData.blockedAnswers.length} réponse{formData.blockedAnswers.length > 1 ? 's' : ''} bloquée{formData.blockedAnswers.length > 1 ? 's' : ''} sur {formData.options.length}.
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Submit Buttons */}
                    <div className="flex gap-3">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (isEdit ? 'Update Field' : 'Create Field')}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/form-fields')}
                            className="rounded-lg bg-gray-200 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Card>
        </div>
    );
}
